/* eslint-disable sonarjs/cognitive-complexity */
import * as React from 'react';
import { Button, Col, Dropdown, DropdownButton, Form, Modal, Row } from 'react-bootstrap';
import { Search, Trash3 } from 'react-bootstrap-icons';
import Draggable from 'react-draggable';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { FormContext } from '@/helper/context';
import { camelToTitle, mapSearchFormToArray, omitKeysWithPrefix, safeJSONParse } from '@/helper/utility';

export enum SearchOperator {
  AND = 'AND',
  OR = 'OR',
  NOT = 'NOT',
  NA = 'NA'
}

export interface SearchFilter {
  type: string;
  uuid: string;
  id?: number;
  name?: string;
  startDate?: string;
  endDate?: string;
  operator: SearchOperator;
}

interface Properties {
  modalTitle: string;
  formId: string;
  filtersAvailable: string[];
  filterLabels?: Record<string, string>;
  renderFilter: (filter: SearchFilter, index: number) => React.ReactNode;
  validateSearchArray: (searchArray: any[]) => Record<string, string>;
  moveRight?: boolean;
  draggable?: boolean;
  disabled?: boolean;
}

export function SearchButtonAdvanced({
  modalTitle,
  formId,
  filtersAvailable,
  filterLabels,
  renderFilter,
  validateSearchArray,
  moveRight = false,
  draggable = false,
  disabled = false
}: Properties) {
  const [modal, setModal] = React.useState(false);
  const [errors, setErrors] = React.useState<any>({});

  const [parameters, setParameters] = useSearchParams();
  const searchParameter = parameters.get('s') || '';
  const searchParsed = searchParameter ? safeJSONParse(decodeURIComponent(searchParameter)) : [];
  const searchArray = Array.isArray(searchParsed)
    ? searchParsed.filter((filter) => filtersAvailable.includes(filter?.type))
    : [];

  const nodeReference = React.useRef(null);
  const position = React.useRef({ x: 0, y: 0 });

  const location = useLocation();
  const navigate = useNavigate();

  const openModal = () => {
    setModal(true);
    setErrors({});
  };
  const closeModal = () => {
    setModal(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const nextSearchArray = mapSearchFormToArray(Object.fromEntries(formData.entries()));

    const errorsUpdate = validateSearchArray(nextSearchArray);

    if (JSON.stringify(errorsUpdate) === '{}') {
      const searchParameter_ = encodeURIComponent(JSON.stringify(nextSearchArray));
      parameters.set('s', searchParameter_);
      parameters.delete('p');

      if (location.pathname.endsWith('/search')) {
        setParameters(parameters);
      } else {
        navigate(location.pathname + '/search?s=' + searchParameter_);
      }

      setModal(false);
    } else {
      setErrors(errorsUpdate);
    }
  };

  function handleStop(event: any, data: any) {
    event.preventDefault();
    position.current = { x: data.x, y: data.y };
  }

  return (
    <React.Fragment>
      <Button
        variant='outline-secondary'
        type='button'
        className={'mb-2 header-button ' + (moveRight ? 'ms-auto' : 'ms-2')}
        onClick={openModal}
      >
        <Search color='#0d6efd' />
      </Button>
      <Modal show={modal} onHide={closeModal} size={'xl'} contentClassName='hide'>
        <Draggable
          handle='.modal-header'
          nodeRef={nodeReference}
          onStop={handleStop}
          bounds={{ top: -50 }}
          defaultPosition={{ x: position.current.x, y: position.current.y }}
          disabled={!draggable}
        >
          <div ref={nodeReference} className={'modal-content' + (draggable ? ' draggable' : '')}>
            <Modal.Header closeButton>
              <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <ModalInner
              disabled={disabled}
              errors={errors}
              setErrors={setErrors}
              handleSubmit={handleSubmit}
              closeModal={closeModal}
              formId={formId}
              filters={searchArray}
              filtersAvailable={filtersAvailable}
              filterLabels={filterLabels}
              renderFilter={renderFilter}
            />
          </div>
        </Draggable>
      </Modal>
    </React.Fragment>
  );
}

interface InnerProperties {
  disabled: boolean;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  errors: any;
  setErrors: (v: any) => void;
  closeModal: () => void;
  formId: string;
  filters: SearchFilter[];
  filtersAvailable: string[];
  filterLabels?: Record<string, string>;
  renderFilter: (filter: SearchFilter, index: number) => React.ReactNode;
}

function ModalInner({
  disabled,
  errors,
  setErrors,
  handleSubmit,
  closeModal,
  formId,
  filters,
  filtersAvailable,
  filterLabels,
  renderFilter
}: InnerProperties) {
  const changeTracker = React.useRef({});
  const [wasChanged, setWasChanged] = React.useState(false);

  return (
    <React.Fragment>
      <Modal.Body>
        <FormContext.Provider value={{ changeTracker, setWasChanged, disabled, errors, setErrors }}>
          <Form id={formId} onSubmit={handleSubmit} noValidate>
            <Row>
              <SearchFilterList
                filters={filters}
                filtersAvailable={filtersAvailable}
                filterLabels={filterLabels}
                renderFilter={renderFilter}
                formId={formId}
              />
            </Row>
          </Form>
        </FormContext.Provider>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' type='button' className='me-2' onClick={closeModal}>
          Cancel
        </Button>
        <Button variant='primary' type='submit' form={formId} disabled={disabled || !wasChanged}>
          Search
        </Button>
      </Modal.Footer>
    </React.Fragment>
  );
}

interface SearchFilterProperties {
  filters?: SearchFilter[];
  filtersAvailable: string[];
  filterLabels?: Record<string, string>;
  renderFilter: (filter: SearchFilter, index: number) => React.ReactNode;
  formId: string;
}

function SearchFilterList({ filters, filtersAvailable, filterLabels, renderFilter, formId }: SearchFilterProperties) {
  const { changeTracker, setWasChanged, errors, setErrors } = React.useContext(FormContext);

  const filtersAddUUID = (filters || []).map((filter) => ({
    ...filter,
    operator: filter.operator || SearchOperator.NA,
    uuid: crypto.randomUUID()
  }));

  const [filtersActive, setFiltersActive] = React.useState<SearchFilter[]>(filtersAddUUID || []);

  const addFilter = (filterType: string) => {
    setFiltersActive([
      ...filtersActive,
      { type: filterType, uuid: crypto.randomUUID(), operator: SearchOperator.AND } as SearchFilter
    ]);

    changeTracker.current = {
      ...changeTracker.current,
      [filterType]: true
    };
    setWasChanged(Object.values(changeTracker.current).includes(true));
  };

  const removeFilter = (filterRemoved: number) => {
    const temporaryFilter = filtersActive.filter((_, index) => index !== filterRemoved);
    setFiltersActive(temporaryFilter);

    setErrors(omitKeysWithPrefix(errors, filterRemoved));

    changeTracker.current = {
      ...changeTracker.current,
      [filterRemoved]: true
    };
    setWasChanged(temporaryFilter.length > 0 ? Object.values(changeTracker.current).includes(true) : false);
  };

  return (
    <React.Fragment>
      {filtersActive.map((filter, index) => {
        return (
          <FilterElement key={filter.uuid} index={index} operator={filter.operator} removeFilter={removeFilter}>
            {renderFilter(filter, index)}
          </FilterElement>
        );
      })}
      <Col xs={{ span: 9, offset: 2 }}>
        <DropdownButton
          id={formId + '-filters'}
          title={'+ Search Filter'}
          variant='outline-secondary'
          className='ms-auto mt-3'
        >
          {filtersAvailable?.map((filterType, index) => {
            // eslint-disable-next-line security/detect-object-injection
            const label = filterLabels?.[filterType] ?? camelToTitle(filterType);
            return (
              <Dropdown.Item key={filterType + index} onClick={() => addFilter(filterType)}>
                {label}
              </Dropdown.Item>
            );
          })}
        </DropdownButton>
      </Col>
    </React.Fragment>
  );
}

interface FilterElementProperties {
  index: number;
  operator: SearchOperator;
  removeFilter: (index: number) => void;
  children: React.ReactNode;
}

function FilterElement({ index, operator, removeFilter, children }: FilterElementProperties) {
  const { changeTracker, setWasChanged } = React.useContext(FormContext);
  const [selected, setSelected] = React.useState(operator);

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as SearchOperator;
    setSelected(value);

    const didChange = value == operator ? false : true;
    changeTracker.current = {
      ...changeTracker.current,
      [index + '_condition']: didChange
    };
    setWasChanged(Object.values(changeTracker.current).includes(true));
  };

  return (
    <Col xs={12}>
      <Row className='align-items-end'>
        <Col xs={2} className='mt-3'>
          {index !== 0 && (
            <Form.Group controlId={index + '_condition'}>
              <Form.Select name={index + '_condition'} value={selected} onChange={onChange}>
                <option value={SearchOperator.AND}>{SearchOperator.AND}</option>
                <option value={SearchOperator.OR}>{SearchOperator.OR}</option>
                <option value={SearchOperator.NOT}>{SearchOperator.NOT}</option>
              </Form.Select>
            </Form.Group>
          )}
        </Col>
        <Col xs={9}>{children}</Col>
        <Col xs={1} className='mt-3'>
          <a onClick={() => removeFilter(index)}>
            <Trash3 color='red' className='search-align-trash' title='Remove Filter' aria-label='remove' />
          </a>
        </Col>
      </Row>
    </Col>
  );
}
